import Autosuggest from 'react-autosuggest';
import React, { useState, useContext } from 'react';
import parse from 'html-react-parser';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { MFBeaconButton } from '../Beacon/BeaconComponents';
import styles from './styles.css';
import { FlowContext } from '../../core/flow-engine';
import {
  highlightSearchTerm,
  renderSuggestionAfter3Chars,
  clean
} from './utils';
import { getCustodianDetailsByIDService } from '../../flows/shared/services/custodianByID';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const noop = () => {};
const PROVIDER_NOT_LISTED = 'My provider is not listed';

export const getSuggestionValue = suggestion => {
  return suggestion.custodianName ? `${suggestion.custodianName}` : suggestion;
};

export const renderInputComponent = inputProps => {
  return (
    <div className="auto-suggest-input">
      <input
        className="quick-search-input"
        id="quick-search-input-1"
        {...inputProps}
      />
      <span className="search-icon" />
      <style jsx>{styles}</style>
    </div>
  );
};

export const renderSuggestion = suggestion => {
  const contents =
    suggestion && suggestion.custodianName ? (
      <div
        id={`custodian${suggestion.csdId}`}
        data-name={`custodian${suggestion.csdId}`}
        className="institution-suggestion"
        data-value={`${suggestion.csdId}`}>
        {parse(
          highlightSearchTerm(suggestion.custodianName, suggestion.search)
        )}
      </div>
    ) : (
      <div
        id="no-result"
        className="institution-suggestion"
        data-value="no results">
        My provider is not listed
      </div>
    );
  return (
    <div className="returned-suggestions">
      {contents}
      <style jsx>{styles}</style>
    </div>
  );
};

// TODO @Abhi [2022-05-01]: fix complexity
/* eslint-disable complexity */
const QuickSearch = ({
  initialValue = '',
  institutions,
  setManualProviderName,
  mode = 'NASU'
}) => {
  const [value, setValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [noAutoSuggest, setNoAutoSuggest] = useState(false);
  const [switchManualEntryProvider, setSwitchManualEntryProvider] = useState(
    false
  );
  const { next, formProps } = useContext(FlowContext);

  const manualProviderNameChanged = ({
    target: { value: manualProviderValue = '' }
  }) => setManualProviderName(manualProviderValue.trim());

  const getCustodianID = custodianName => {
    return institutions.find(
      ({ custodianName: name }) => name === custodianName
    ).csdId;
  };

  const onSuggestionSelected = async (event, { suggestionValue }) => {
    let custodianID = '';

    const custodianName = suggestionValue;

    if (mode === 'FONASU' && custodianName === PROVIDER_NOT_LISTED) {
      setNoAutoSuggest(true);
      setSwitchManualEntryProvider(false);
      return;
    }
    if (custodianName === PROVIDER_NOT_LISTED) {
      setNoAutoSuggest(true);
      return;
    }

    custodianID = getCustodianID(custodianName);

    if (mode === 'FONASU') {
      const getCustodianDetails = async custID => {
        const custodianInfo = await getCustodianDetailsByIDService(custID);
        return custodianInfo;
      };

      const custodianDetails = await getCustodianDetails(custodianID);

      const {
        branchAddressRequired,
        toraParticipant,
        customerServicePhone,
        regularAddressLine1,
        regularAddressLine2,
        regularAddressCity,
        regularAddressState,
        regularAddressZip,
        funds
      } = custodianDetails;

      setManualProviderName({
        branchAddressRequired,
        custodianID,
        custodianName,
        toraParticipant,
        customerServicePhone,
        regularAddressLine1,
        regularAddressLine2,
        regularAddressCity,
        regularAddressState,
        regularAddressZip,
        funds
      });
      return;
    }

    next({
      custodianID,
      custodianName
    })();
  };

  const getSuggestions = search => {
    const cleanSearchValue = clean(search.trim());
    if (cleanSearchValue.length > 0) {
      const regex = new RegExp(search, 'i');
      const funds = institutions
        .filter(institution => regex.test(clean(institution.custodianName)))
        .map(fund => ({ ...fund, search: cleanSearchValue }));

      return funds.length > 0 ? funds : [PROVIDER_NOT_LISTED];
    }
    return [];
  };

  const onSuggestionsClearRequested = () => setSuggestions([]);
  const onSuggestionsFetchRequested = ({ value: newValue }) =>
    setSuggestions(getSuggestions(newValue));

  const onChange = (event, { newValue }) => setValue(newValue);

  const inputProps = {
    placeholder: 'Search for your provider',
    value,
    onChange
  };

  return (
    <div>
      {mode === 'FONASU' && value === PROVIDER_NOT_LISTED && (
        <div>
          <MFBeaconButton
            id="fonasuProviderSearchBtn"
            data-name="fonasuProviderSearchBtn"
            onClick={() => {
              setValue('');
              setSwitchManualEntryProvider(true);
            }}
            variant="tertiary"
            label="Search for provider"
          />
        </div>
      )}
      {mode === 'FONASU' &&
        (noAutoSuggest && !switchManualEntryProvider ? (
          <InputField
            type="text"
            placeholder="Enter your plan provider's name"
            onChange={manualProviderNameChanged}
            {...formProps.manualProviderName}
            defaultValue=""
          />
        ) : (
          <Autosuggest
            data-name="quick-search-input"
            suggestions={suggestions}
            shouldRenderSuggestions={renderSuggestionAfter3Chars}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            getSuggestions={getSuggestions}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            renderInputComponent={renderInputComponent}
          />
        ))}
      {mode !== 'FONASU' &&
        (noAutoSuggest ? (
          <InputField
            type="text"
            placeholder="Enter your plan provider's name"
            onChange={manualProviderNameChanged}
            {...formProps.manualProviderName}
            defaultValue=""
            data-name={generateGeneralTestID('QuickSearch', 'ManualProvider')}
          />
        ) : (
          <Autosuggest
            suggestions={suggestions}
            shouldRenderSuggestions={renderSuggestionAfter3Chars}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            getSuggestions={getSuggestions}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            renderInputComponent={renderInputComponent}
          />
        ))}
      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable complexity */

QuickSearch.defaultProps = {
  initialValue: '',
  institutions: [],
  setManualProviderName: noop,
  mode: 'NASU'
};

export default QuickSearch;
